import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Link,
} from '@mui/material';
import { COOKIE_POLICY_URL } from '../constants';
import { CookieCategory } from '../hooks/useCookieConsent';
import ModalCategorySection from './ModalCategorySection';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 500,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  button: {
    whiteSpace: 'nowrap',
    '&:focus-visible': {
      outline: '2px solid black',
      outlineOffset: 2,
    },
  },
  link: {
    color: 'inherit',
    textDecorationColor: 'inherit',
  },
}));

const CookiePreferencesModal = ({ open, onClose, consent }) => {
  const classes = useStyles();

  const handleSave = () => {
    consent?.saveChanges?.();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="cookie-preferences-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="cookie-preferences-title">
        Cookie Preferences
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Cookies are small text files that are placed on your computer by
          websites that you visit. Websites use cookies to help users navigate
          efficiently and perform certain functions. Cookies that are required
          for the website to operate properly (“Essential Cookies”) cannot be
          disabled. All other cookies need to be approved before they can be set
          in the browser.
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          We use cookies to provide you with the best experience. By using this
          site, you agree to our use of cookies in accordance with our{' '}
          <Link
            href={COOKIE_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            Cookie Policy
          </Link>
          .
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You can change your consent to cookie usage at any time.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <ModalCategorySection
          label="Essential Cookies"
          description="These cookies are essential for you to be able to navigate our website and use its features. Without these cookies, the website would not function properly."
          checked
          required
          disabled
        />
        <ModalCategorySection
          label="Performance Cookies"
          description="These cookies track your interactions with our website, such as which pages you visit frequently and how you navigate through the site. While the data collected by these cookies does not directly identify you, it may indirectly identify you since it is usually linked to a pseudonymous identifier associated with the device you use to access our website."
          checked={consent?.state?.draft?.[CookieCategory.PERFORMANCE]}
          onChange={() => consent?.toggleCategory?.(CookieCategory.PERFORMANCE)}
        />
        {/* Hidden until we figure out how to use this category

        <ModalCategorySection
          label="Functionality Cookies"
          description="These cookies allow our website to remember the choices you make (such as your user name, language, last action, and search preferences) and provide enhanced, more personalized features."
          checked={consent?.state?.draft?.[CookieCategory.FUNCTIONALITY]}
          onChange={() =>
            consent?.toggleCategory?.(CookieCategory.FUNCTIONALITY)
          }
        />
        */}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} className={classes.button}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          className={classes.button}
        >
          Save & Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CookiePreferencesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  consent: PropTypes.object.isRequired,
};

CookiePreferencesModal.defaultProps = {
  open: false,
  onClose: () => {},
  consent: {},
};

export default CookiePreferencesModal;
